/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, DataTableComponent, UNPLACEHOLDER, UtilService } from '@dm/core';
import { NEW_DETAIL_ERROR_NAME } from 'app/common/constants';
import { SWITCH } from 'app/config';

import { type ErrorData, type StepData, Status, TaskCenterService, TaskData } from '../task-center.service';
import { TaskStrService } from '../task-str.service';

@Component({
    selector: 'app-task-detail',
    templateUrl: './task-detail.component.html',
    styleUrls: ['./task-detail.component.scss'],
})
export class TaskDetailComponent implements OnInit, OnChanges {
    @Input() show: boolean;
    @Input() task: TaskData;

    @Output() showChange = new EventEmitter<any>();
    @Output() onClose = new EventEmitter<any>();

    @ViewChild('dt') table: DataTableComponent;

    dataRest: Rest;
    tableConfig: TableConfig;
    columnDefs: ColumnDef[];
    stepNameOpt: { [key: string]: string | number } = {};
    i18nLabel = this.str.i18nLabel;
    statusText = this.str.statusText;
    beforeTaskID: any;
    showErrorDetail = false;
    errorData: ErrorData;

    // 传给模板使用
    taskStatus = Status;

    SWITCH = SWITCH;

    constructor(
        public str: TaskStrService,
        private service: TaskCenterService,
        private utilService: UtilService,
    ) { }

    ngOnInit(): void {
        this.initTableConfig();

        this.updateTaskDetailInfo(this.task);
    }

    // 当输入属性外部输入发生变化时触发，组件内修改不触发
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.task && this.show) {
            this.updateTaskDetailInfo(this.task);
        }
    }

    updateTaskDetailInfo(task: TaskData): void {
        if (!task) {
            return;
        }
        // 切换任务后清空过滤，避免名称过滤问题
        if (this.table) {
            this.table.resetFilters();
        }
        this.dataRest = this.service.getTaskStepsRest(task.id);
    }

    initTableConfig(): void {
        const i18nLabel = this.i18nLabel;

        this.tableConfig = {
            dataKey: 'id',
            pollingPeriod: 15,
            rowsPerPageOptions: null,
            pageStyle: 'simple',
            order: { serialNum: 'desc' },
            beforeShow: (data): any[] => {
                // 子步骤表格刷新时，同步刷新一下任务信息
                this.updateTask();
                if (!this.beforeTaskID || this.beforeTaskID !== this.task.id) {
                    this.stepNameOpt = {};
                    this.beforeTaskID = this.task.id;
                }

                // @todo 超过100步时会有bug
                data.forEach((item) => {
                    this.stepNameOpt[item.name] = item.displayName;
                });

                this.table?.setFilterOptions('name', this.stepNameOpt);
                return data;
            },
        };

        this.columnDefs = [
            {
                header: i18nLabel.numberCol,
                field: 'serialNum',
                columnFilter: true,
                sortable: true,
                hidden: true,
            },
            {
                header: i18nLabel.stepName,
                field: 'name',
                filterType: 'dropdown',
                options: this.stepNameOpt,
                render: (value, col, data: TaskData) => data.displayName,
            },
            {
                field: 'id',
                header: 'ID',
                columnFilter: true,
                sortable: true,
                hidden: true,
            },
            {
                header: i18nLabel.object,
                field: 'object',
                columnFilter: true,
                render: (value) => value.objectList && value.objectList.length ? value.objectList[value.currentExecuteIndex] : UNPLACEHOLDER,
            },
            {
                header: i18nLabel.stepStatus,
                field: 'execStatus',
                filterType: 'dropdown',
                options: this.str.taskStatusOptions,
                columnFilter: true,
            },
            {
                header: i18nLabel.startTime,
                field: 'startTime',
                filterType: 'dateTimeRange',
                columnFilter: true,
                sortable: true,
                render: (value) => this.str.renderTime(Number(value)),
            },
            {
                header: i18nLabel.endTime,
                field: 'endTime',
                filterType: 'dateTimeRange',
                columnFilter: true,
                sortable: true,
                render: (value) => this.str.renderTime(Number(value)),
            },
            {
                header: i18nLabel.duration,
                field: 'duration',
                width: '100px',
                columnFilter: true,
                render: (value, col, data): string | number => {
                    const duration = this.str.getDuration(data.startTime, data.endTime);
                    return this.str.renderDuration(duration);
                },
            },
        ];
    }

    renderTime = (value): string => this.str.renderTime(Number(value) || 0);
    renderDuration = (task: TaskData): string => this.str.renderDuration(this.str.getDuration(task.startTime, task.endTime));

    onRefresh(): void {
        this.table.refresh();
    }

    updateTask(): void {
        this.service.getTask(this.task.id).subscribe(({ data }) => {
            const { currentStepIndex, stepCount } = data.description || { currentStepIndex: 0, stepCount: 1 };
            this.task = {
                ...data,
                creator: data.userSession || UNPLACEHOLDER,
                progress: Math.floor(currentStepIndex / stepCount * 100),
            };
        });
    }

    updataStepNameOptions(): void {
        if (this.table && this.task.description.stepNames && this.task.description.stepNames.length) {
            const stepNameOptions: any = {};
            this.task.description.stepNames.forEach((name) => {
                stepNameOptions[name] = this.str.stepNameOptions[name];
            });
            this.table.setFilterOptions('name', stepNameOptions);
        }
    }

    onShowErrorDetail(step: StepData): void {
        this.showErrorDetail = true;
        const { code, description, suggestion } = step.description;

        // 新详情模板description返的是数组
        const useNewTemplate = Array.isArray(description) && NEW_DETAIL_ERROR_NAME.includes(step.name);
        this.errorData = {
            name: useNewTemplate ?
                this.str.preCheckStepFailedTitle(step.displayName) : this.str.stepFailedTitle(step.displayName),
            code,
            description,
            suggestion,
            useNewTemplate,
        };
    }

    close(): void {
        this.stepNameOpt = {};
        this.show = false;
    }

    onShow(): void {
        if (this.table) {
            this.table.recoverPolling();
        }
    }

    onHide(): void {
        if (this.table) {
            this.table.stopPolling();
        }
        this.showChange.emit(this.show);
        this.onClose.emit(this.show);
    }

    replaceBlock(objName): string {
        return this.utilService.encodeHTML(objName).replace(/ /g, '&nbsp;');
    }
}
